/**
 * `<MyWorkView/>` — the member home "My work" (B5.1) + its empty state (B8.1).
 *
 * Issue #761 (Phase 2). The member's OWN slice of the plan: the nodes
 * assigned to *them*, oriented around "what's on me" — never a feed of what
 * others did, never a per-person tally (§A9 brand firewall). The whole tree
 * is still visible to everyone (§A8); this is just the calm, focused entry
 * point into the member's part of it.
 *
 * Faithful to `B5-1-my-work.svg`:
 *   - heading "Your parts of "<root outcome>"".
 *   - one row per assigned node: a state dot, the title, a state pill, the
 *     "in: <parent>" context, and an optional target-date chip.
 *   - a single FOCAL callout for the one in-progress item — "This is the one
 *     thing on your plate right now." + an "open →" that opens that node's
 *     drawer (the §A1 focal-element rule: one anchor, not a uniform grid).
 *   - footer: "See where everything fits →" + a "Full map →" button.
 *
 * When the member has nothing assigned, the whole view collapses to the
 * calm B8.1 empty card ("Nothing's assigned to you yet.").
 *
 * Editorial revamp (#1043, Wave 3, B5.1): theme-aware via `useMapTheme()`.
 * The `task_editorial_ui` flag stays OFF for every team (default LEGACY) until
 * the whole revamp lands, so:
 *   - LEGACY (flag off): byte-identical to the shipped soft-mint home — the
 *     blue "This is the one thing…" focal box, the mint rows, the indigo
 *     "Full map →".
 *   - EDITORIAL (flag on): the wireframe `V_B51` — a mono `YOUR PARTS OF "…"`
 *     kicker over a serif-italic "What's on your plate today." masthead; the
 *     focal item pulled forward as the FOCAL ink card (`c.ink` fill, cream
 *     text, a mono `THE ONE THING ON YOUR PLATE` label + a mono target chip, a
 *     state chip + serif-italic title + a ghost "open →"); an `ALSO YOURS`
 *     `.t-card` list (hairline dividers, chip · title · "in: <parent>" ·
 *     "open →"); a `MENTIONED YOU` `.t-card`; a dashed-rule footer. RE-SKIN,
 *     not rebuild: every `data-testid`, copy, and behaviour is preserved.
 *
 * Presentational: it receives the already-filtered "my nodes" + callbacks
 * (open a node, go to the full map). The filtering lives in the parent
 * (`MemberMapView`) so the one data source stays there.
 */

import { useMapTheme } from "../theme/MapThemeContext";
import type { TreeNode } from "../tree/buildTree";
import { PALETTE } from "../tree/palette";
import { StatusChip, StatusDot } from "./StatusChip";

export interface MyWorkItem {
  /** The node assigned to the current member. */
  node: TreeNode;
  /** The node's parent title (the "in: …" context), or null for a root. */
  parentTitle: string | null;
}

export interface MyWorkViewProps {
  /** The nodes assigned to the current member, in tree order. */
  items: MyWorkItem[];
  /** The team's top outcome title, for the "Your parts of …" heading. */
  rootTitle: string | null;
  /** Open a node's detail drawer (B5.3). */
  onOpenNode: (id: string) => void;
  /** Switch to the full-map (read) view (B5.2). */
  onSeeFullMap: () => void;
}

const INDIGO = "#4F46E5";
const TARGET_AMBER = "#D97706";

export function MyWorkView({ items, rootTitle, onOpenNode, onSeeFullMap }: MyWorkViewProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  if (items.length === 0) {
    return <EmptyMember onSeeFullMap={onSeeFullMap} />;
  }

  // The focal item: the one in-progress task is "the one thing on your plate
  // right now". If none is in progress, the callout simply doesn't show — we
  // never invent a focal where there isn't one.
  const focal = items.find((it) => it.node.state === "in_progress") ?? null;

  // The first not-yet-started task — the "what do I do next?" anchor (#980) for
  // a member whose work is assigned but nothing is in progress. Only used when
  // there is no focal; never invents urgency where there is none.
  const nextToStart = items.find((it) => it.node.state === "planned") ?? null;

  // Editorial (`V_B51`) pulls the focal item FORWARD into the ink "one thing"
  // card at the top, so the rows list ("ALSO YOURS") shows the rest. Legacy
  // keeps every assigned node in the one list with the focal row emphasised in
  // place. So the list contents fork, but the data source + ordering don't.
  const rowItems = editorial ? items.filter((it) => it.node.id !== focal?.node.id) : items;

  return (
    <div
      data-testid="my-work-view"
      className="h-full overflow-auto px-6 py-5"
      style={{
        backgroundColor: c.canvas,
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      {editorial ? (
        <div className="mx-auto w-full max-w-[880px]">
          {/* Mono kicker + serif-italic masthead (`V_B51`). The kicker carries
              the same "Your parts of …" outcome, uppercased as a mono ISSUE
              label; the masthead is the wireframe's "What's on your plate
              today." — a calm, member-facing display line (the legacy heading
              text is preserved verbatim below under the legacy branch). */}
          <div
            data-testid="my-work-kicker"
            className="text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.08em", color: c.muted }}
          >
            Your parts of {rootTitle ? `“${rootTitle}”` : "the plan"}
          </div>
          <h2
            className="mt-1.5 mb-6 text-[30px] italic leading-tight"
            style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
          >
            What&#8217;s on your plate today.
          </h2>

          {/* The one thing pulled forward — the FOCAL ink card (`V_B51`). Ink
              fill, cream text; a mono "THE ONE THING ON YOUR PLATE" label + a
              mono target chip; a state chip + serif-italic title + a ghost
              "open →". One anchor, the member's current focus (§A1). */}
          {focal && (
            <div
              data-testid="my-work-focal"
              className="mb-7 px-5 py-5"
              style={{ backgroundColor: c.ink, color: c.canvas, borderRadius: theme.radii.xl }}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <span
                  className="text-[10px] uppercase"
                  style={{
                    fontFamily: theme.fonts.mono,
                    letterSpacing: "0.08em",
                    color: c.rollupTrack,
                  }}
                >
                  This is the one thing on your plate right now.
                </span>
                {focal.node.targetDate && (
                  <span
                    data-testid="my-work-target"
                    className="text-[10.5px]"
                    style={{
                      fontFamily: theme.fonts.mono,
                      letterSpacing: "0.06em",
                      color: c.rollupTrack,
                    }}
                  >
                    &#183; target {formatTarget(focal.node.targetDate)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3.5">
                <StatusChip state={focal.node.state} />
                <span className="text-[26px] italic" style={{ fontFamily: theme.fonts.serif }}>
                  {focal.node.title}
                </span>
                <button
                  type="button"
                  data-testid="my-work-focal-open"
                  onClick={() => onOpenNode(focal.node.id)}
                  className="ml-auto shrink-0 rounded-md border px-3 py-1 text-[11.5px] transition-colors hover:opacity-90"
                  style={{ color: c.canvas, borderColor: c.faint }}
                >
                  open &#8594;
                </button>
              </div>
              {focal.parentTitle && (
                <div className="mt-2 text-xs" style={{ color: c.rollupTrack }}>
                  in: {focal.parentTitle}
                </div>
              )}
            </div>
          )}

          {/* "What do I do next?" (#980) — the calm next-step line, editorial
              skin. Same copy + behaviour; self-hides once something is in
              progress (the focal). */}
          {!focal && nextToStart && (
            <div
              data-testid="my-work-next-step"
              className="mb-7 flex items-center justify-between gap-3 rounded-md border px-5 py-3"
              style={{ backgroundColor: c.tealSoft, borderColor: c.teal, color: c.tealInk }}
            >
              <span className="text-xs">Ready to start? Open a task and set it In progress.</span>
              <button
                type="button"
                data-testid="my-work-next-step-open"
                onClick={() => onOpenNode(nextToStart.node.id)}
                className="shrink-0 rounded-md px-4 py-1.5 text-[11.5px] font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: c.ink, color: c.canvas }}
              >
                open &#8594;
              </button>
            </div>
          )}

          {/* ALSO YOURS — the rest of the member's assigned nodes on one
              `.t-card` with hairline dividers. (When there's no focal, this is
              every assigned node; otherwise the focal is pulled forward above.) */}
          <div
            data-testid="my-work-also-yours-label"
            className="text-[10px] uppercase"
            style={{
              fontFamily: theme.fonts.mono,
              letterSpacing: "0.1em",
              color: c.muted,
              fontWeight: 500,
            }}
          >
            Also yours
          </div>
          <ul
            className="mt-2.5 overflow-hidden rounded-[10px] border"
            data-testid="my-work-list"
            style={{ backgroundColor: c.cardBg, borderColor: c.rule }}
          >
            {rowItems.map((item, i) => (
              <MyWorkRow
                key={item.node.id}
                item={item}
                focal={false}
                editorial
                last={i === rowItems.length - 1}
                onOpen={() => onOpenNode(item.node.id)}
              />
            ))}
          </ul>

          {/* MENTIONED YOU — a calm seam for the #767 notes layer (own mentions
              only). No CRUD yet, so it states the fact plainly. */}
          <div className="mt-7">
            <div
              className="text-[10px] uppercase"
              style={{
                fontFamily: theme.fonts.mono,
                letterSpacing: "0.1em",
                color: c.muted,
                fontWeight: 500,
              }}
            >
              Mentioned you
            </div>
            <div
              className="mt-2.5 rounded-[10px] border px-4 py-3"
              style={{ backgroundColor: c.cardBg, borderColor: c.rule }}
            >
              <p className="text-[12.5px]" style={{ color: c.muted }}>
                When a teammate mentions you in a note, it will show up here.
              </p>
            </div>
          </div>

          {/* Footer: see where everything fits → full map (B5.2), on a dashed
              rule per the wireframe. */}
          <div
            className="mt-9 flex items-center gap-3 border-t border-dashed pt-4.5"
            style={{ borderColor: c.rule }}
          >
            <span className="text-xs italic" style={{ color: c.muted }}>
              See where everything fits &#8594;
            </span>
            <button
              type="button"
              data-testid="my-work-full-map"
              onClick={onSeeFullMap}
              className="ml-auto rounded-md border px-3 py-1 text-[11.5px] transition-colors hover:opacity-90"
              style={{ borderColor: c.rule2, color: c.ink }}
            >
              Full map &#8594;
            </button>
          </div>
        </div>
      ) : (
        <>
          <h2 className="text-[13px] font-bold" style={{ color: PALETTE.ink }}>
            Your parts of {rootTitle ? `“${rootTitle}”` : "the plan"}
          </h2>

          <ul className="mt-3 flex flex-col gap-3" data-testid="my-work-list">
            {items.map((item) => (
              <MyWorkRow
                key={item.node.id}
                item={item}
                focal={focal?.node.id === item.node.id}
                onOpen={() => onOpenNode(item.node.id)}
              />
            ))}
          </ul>

          {/* The single focal callout — one anchor, the member's current focus. */}
          {focal && (
            <div
              data-testid="my-work-focal"
              className="mt-3 flex items-center justify-between gap-3 rounded-lg px-5 py-3"
              style={{ backgroundColor: "#DBEAFE", border: "1px solid #93C5FD" }}
            >
              <span className="text-xs" style={{ color: "#1E40AF" }}>
                This is the one thing on your plate right now.
              </span>
              <button
                type="button"
                data-testid="my-work-focal-open"
                onClick={() => onOpenNode(focal.node.id)}
                className="shrink-0 rounded-md px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "#2563EB" }}
              >
                open &#8594;
              </button>
            </div>
          )}

          {/* "What do I do next?" for a member (#980) — when work is assigned but
              nothing is in progress yet, there's no focal callout above, so a member
              can be unsure what to do. A calm next-step line points at the first
              task to open + begin. Factual + supportive, never implying they're
              behind (§A9). Self-hides as soon as something is in progress (focal). */}
          {!focal && nextToStart && (
            <div
              data-testid="my-work-next-step"
              className="mt-3 flex items-center justify-between gap-3 rounded-lg px-5 py-3"
              style={{ backgroundColor: "#F7FFF9", border: `1px solid ${PALETTE.softBorder}` }}
            >
              <span className="text-xs" style={{ color: PALETTE.slate }}>
                Ready to start? Open a task and set it In progress.
              </span>
              <button
                type="button"
                data-testid="my-work-next-step-open"
                onClick={() => onOpenNode(nextToStart.node.id)}
                className="shrink-0 rounded-md px-4 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: PALETTE.teal }}
              >
                open &#8594;
              </button>
            </div>
          )}

          {/* "Mentioned you" — a calm seam for the #767 notes layer. The member's
              OWN mentions only (not a feed about others). No CRUD is built yet, so
              this states the fact plainly rather than faking activity. */}
          <div className="mt-6">
            <h3 className="text-[13px] font-bold" style={{ color: PALETTE.ink }}>
              Mentioned you
            </h3>
            <p className="mt-2 text-[11px]" style={{ color: PALETTE.faint }}>
              When a teammate mentions you in a note, it will show up here.
            </p>
          </div>

          {/* Footer: see where everything fits → full map (B5.2). */}
          <div
            className="mt-6 flex items-center gap-4 border-t pt-4"
            style={{ borderColor: "#F3F4F6" }}
          >
            <span className="text-[11px] italic" style={{ color: PALETTE.faint }}>
              See where everything fits &#8594;
            </span>
            <button
              type="button"
              data-testid="my-work-full-map"
              onClick={onSeeFullMap}
              className="rounded-md border px-4 py-1.5 text-[11px] font-semibold transition-colors hover:bg-indigo-50"
              style={{ borderColor: INDIGO, color: INDIGO, backgroundColor: "#EEF2FF" }}
            >
              Full map &#8594;
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MyWorkRow({
  item,
  focal,
  editorial = false,
  last = false,
  onOpen,
}: {
  item: MyWorkItem;
  focal: boolean;
  editorial?: boolean;
  last?: boolean;
  onOpen: () => void;
}) {
  const { node, parentTitle } = item;
  const theme = useMapTheme();
  const c = theme.palette;

  if (editorial) {
    // ALSO YOURS row (`V_B51`): a quiet `.t-card` row — chip · title ·
    // "in: <parent>" · "open →" — divided by hairlines. Calm + factual; the
    // whole row opens the node's drawer.
    return (
      <li>
        <button
          type="button"
          data-testid="my-work-row"
          data-node-id={node.id}
          onClick={onOpen}
          className="flex w-full items-center gap-3 px-4.5 py-3.5 text-left transition-colors hover:opacity-90"
          style={{
            borderBottom: last ? undefined : `1px solid ${c.rule}`,
            fontFamily: theme.fonts.sans,
          }}
        >
          <StatusChip state={node.state} />
          <span className="min-w-0 truncate text-[13.5px]" style={{ color: c.ink }}>
            {node.title}
          </span>
          {parentTitle && (
            <span className="shrink-0 text-[11.5px]" style={{ color: c.muted }}>
              in: {parentTitle}
            </span>
          )}
          {node.targetDate && (
            <span
              className="shrink-0 text-[11px]"
              style={{ fontFamily: theme.fonts.mono, color: c.muted }}
              data-testid="my-work-target"
            >
              &#128197; target {formatTarget(node.targetDate)}
            </span>
          )}
          <span
            className="ml-auto shrink-0 text-[11.5px] underline underline-offset-2"
            style={{ color: c.teal }}
          >
            open &#8594;
          </span>
        </button>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        data-testid="my-work-row"
        data-node-id={node.id}
        data-focal={focal || undefined}
        onClick={onOpen}
        className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-shadow"
        style={{
          backgroundColor: focal ? "#EEF2FF" : "#F7FFF9",
          border: focal ? `1.5px solid ${INDIGO}` : "1px solid #D1FAE5",
          boxShadow: focal ? "0 2px 6px rgba(31,122,110,0.14)" : undefined,
        }}
      >
        <StatusDot state={node.state} />
        <span
          className="min-w-0 flex-1 truncate text-xs"
          style={{ color: PALETTE.ink, fontWeight: focal ? 700 : 600 }}
        >
          {node.title}
        </span>
        <StatusChip state={node.state} />
        {parentTitle && (
          <span className="shrink-0 text-[11px]" style={{ color: "#9CA3AF" }}>
            in: {parentTitle}
          </span>
        )}
        {node.targetDate && (
          <span
            className="shrink-0 text-[11px]"
            style={{ color: TARGET_AMBER }}
            data-testid="my-work-target"
          >
            &#128197; target {formatTarget(node.targetDate)}
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * `<EmptyMember/>` — the no-assigned-work state (B8.1). Calm + factual: never
 * evaluative, never implying the member is behind. Faithful to
 * `B8-1-empty-member.svg`.
 *
 * Editorial revamp (#1043, Wave 3): theme-aware. LEGACY (flag off) is
 * byte-identical to the shipped soft-mint card; EDITORIAL (flag on) is the
 * warm `.t-card` (cream surface, hairline border, soft shadow) with the same
 * calm copy + the ink "See the full map →". RE-SKIN, not rebuild.
 */
export function EmptyMember({ onSeeFullMap }: { onSeeFullMap: () => void }) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  return (
    <div
      data-testid="empty-member"
      className="flex h-full w-full flex-col items-center justify-center px-6"
      style={{
        backgroundColor: c.canvas,
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      <div
        className="flex w-full max-w-md flex-col items-center gap-2 bg-white px-8 py-7 text-center"
        style={{
          borderRadius: editorial ? theme.radii.lg : 12,
          border: `1px solid ${editorial ? c.rule : PALETTE.softBorder}`,
          backgroundColor: editorial ? c.cardBg : undefined,
          boxShadow: editorial ? "0 1px 2px rgba(0,0,0,0.03)" : "0 2px 8px rgba(31,122,110,0.12)",
        }}
      >
        <p className="text-sm font-semibold" style={{ color: editorial ? c.ink : PALETTE.muted }}>
          Nothing&#8217;s assigned to you yet.
        </p>
        <p className="text-xs" style={{ color: editorial ? c.muted : "#9CA3AF" }}>
          When the team adds you to a task, it shows up here.
        </p>
        <button
          type="button"
          data-testid="empty-member-full-map"
          onClick={onSeeFullMap}
          className={`mt-1 rounded-md border px-4 py-1.5 text-[11px] font-semibold transition-colors ${
            editorial ? "hover:opacity-90" : "hover:bg-indigo-50"
          }`}
          style={
            editorial
              ? { borderColor: c.rule2, color: c.ink }
              : { borderColor: INDIGO, color: INDIGO, backgroundColor: "#EEF2FF" }
          }
        >
          See the full map &#8594;
        </button>
      </div>
    </div>
  );
}

/** "May 24" style short target. Falls back to the raw string if unparseable. */
function formatTarget(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
